import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  SearchInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { ExclamationCircleIcon, SearchIcon, SyncAltIcon, TagIcon } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import CenteredSpinner from '@app/CenteredSpinner';
import { useTenantCatalog } from '../../hooks/useTenantCatalog';
import { useTenantCategories } from '../../hooks/useTenantCategories';
import { formatProductPrice, formatRecurring } from '../../utils/money';
import type { ScalarPricing, TenantCatalogProduct } from '../../types/models/TenantCatalogProduct';

// Three columns: Product / Billing / Unit price — the group-header row spans all of them.
const COLUMN_COUNT = 3;

// Only scalar products render in this catalog. Matrix (domain-operation) products are excluded up front
// (see `scalarProducts` below), so narrow the type once here and carry it through the display rows.
type ScalarCatalogProduct = TenantCatalogProduct & { pricing: ScalarPricing };
const isScalarProduct = (p: TenantCatalogProduct): p is ScalarCatalogProduct => p.pricing.model === 'scalar';

// A flattened display row: either a category group header or a product, interleaved so the table
// renders category groups by walking category-id order (id = display order).
type DisplayRow =
  | { kind: 'group'; categoryId: number; categoryName: string; count: number }
  | { kind: 'product'; product: ScalarCatalogProduct };

/**
 * The read-only, category-grouped product price catalog for the in-scope client (their own prices, or
 * the impersonated client's — the /prices/* routes resolve via the impersonation header; a clientless
 * caller gets platform defaults). It mirrors the staff Admin → Products and Prices layout (category
 * group-header rows + product rows) but strips every write affordance: no editors, kebabs, modals, or
 * mutations. Editing lives only in the Admin area.
 *
 * Domain operations are matrix-priced (per-TLD) and are OWNED by the sibling Domains tab; this catalog
 * excludes them via the authoritative `pricing.model` discriminator (the scalar-vs-matrix split mirrors
 * how Admin splits the two into separate screens), so only scalar products appear here. A "Custom" label
 * marks a negotiated client-specific price.
 */
function TenantProductPricesTab() {
  const { products, loading: catalogLoading, error: catalogError, refetch: refetchCatalog } = useTenantCatalog();
  // Categories drive the section names; a failure here is non-fatal (grouping falls back to the
  // embedded category name), so it never blanks the catalog — only the products fetch is essential.
  const { categories, error: categoriesError, refetch: refetchCategories } = useTenantCategories();

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the search so filtering doesn't run on every keystroke (CLAUDE.md: 400ms).
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchValue), 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const refetch = useCallback(
    () => Promise.all([refetchCatalog(), refetchCategories()]).then(() => undefined),
    [refetchCatalog, refetchCategories],
  );

  // Exclude matrix (domain-operation) products entirely — the Domains tab owns per-TLD pricing. Keying
  // on `pricing.model` (not a DOMAIN_-prefix guess) is the reliable signal per the backend contract.
  const scalarProducts = useMemo(() => products.filter(isScalarProduct), [products]);

  // Client-side filter on label + product_key — the catalog is small (one GET, ~tens of rows).
  const filteredProducts = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) {
      return scalarProducts;
    }
    return scalarProducts.filter((p) => p.label.toLowerCase().includes(q) || p.product_key.toLowerCase().includes(q));
  }, [scalarProducts, debouncedSearch]);

  // Section names come from GET /prices/categories (id = display order). Falls back to the product's
  // embedded category name (and finally "Uncategorized" on a dangling FK), so a categories hiccup never
  // breaks grouping.
  const categoryNames = useMemo(() => {
    const map = new Map<number, string>();
    categories.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [categories]);

  // Group filtered scalar products by category id, then emit groups in category-id order (= display
  // order) with products label-sorted. Only categories with ≥1 matching product are emitted, so empty
  // categories never render — excluding all matrix products makes the "Domain Name" section vanish here
  // naturally (it lives on the Domains tab instead).
  const displayRows = useMemo<DisplayRow[]>(() => {
    const groups = new Map<number, ScalarCatalogProduct[]>();
    filteredProducts.forEach((p) => {
      const existing = groups.get(p.category.id);
      if (existing) {
        existing.push(p);
      } else {
        groups.set(p.category.id, [p]);
      }
    });
    const rows: DisplayRow[] = [];
    Array.from(groups.entries())
      .sort(([a], [b]) => a - b)
      .forEach(([categoryId, group]) => {
        const name = categoryNames.get(categoryId) ?? group[0].category.name ?? 'Uncategorized';
        rows.push({ kind: 'group', categoryId, categoryName: name, count: group.length });
        [...group]
          .sort((x, y) => x.label.localeCompare(y.label))
          .forEach((product) => rows.push({ kind: 'product', product }));
      });
    return rows;
  }, [filteredProducts, categoryNames]);

  // Full spinner/error only on a cold load; a warm Refresh keeps the table and surfaces any error
  // inline (this page has no notification drawer, so a silent failure would be invisible). Gate on the
  // catalog (the essential payload) — a slow/failed categories call must not hold the whole tab hostage.
  if (catalogLoading && products.length === 0) {
    return <CenteredSpinner />;
  }

  if (catalogError && products.length === 0) {
    return (
      <EmptyState
        titleText="Unable to load prices"
        icon={ExclamationCircleIcon}
        variant="lg"
        headingLevel="h2"
        status="danger"
      >
        <EmptyStateBody>{catalogError}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={() => void refetch()}>
              Retry
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  if (scalarProducts.length === 0) {
    return (
      <EmptyState titleText="No prices available yet" icon={TagIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>There are no priced products to show for your organization yet.</EmptyStateBody>
      </EmptyState>
    );
  }

  const renderPriceCell = (pricing: ScalarPricing, recurring: string) => {
    if (pricing.unit_price === null) {
      // Unpriced (no default set yet) → a muted em-dash with a "price on request" hint. The tooltip
      // trigger is focusable so keyboard/screen-reader users can reach it too.
      return (
        <Tooltip content="Price available on request">
          <span tabIndex={0}>
            <Content component="small">—</Content>
          </span>
        </Tooltip>
      );
    }
    return (
      <>
        {formatProductPrice(pricing.unit_price, recurring)}
        {pricing.is_custom && (
          <Label isCompact color="blue" className="pf-v6-u-ml-xs">
            Custom
          </Label>
        )}
      </>
    );
  };

  return (
    <>
      {/* Catalog and categories are separate fetches, so surface their failures separately. A
          catalogError reaching this body is ALWAYS a failed warm refresh (a cold catalog failure
          returns the fatal empty state above, before this render, with no products), so its "refresh"
          wording is accurate. A categoriesError is non-fatal — grouping falls back to each product's
          embedded category name — and can occur on the very first load, so it gets its own honest,
          non-alarming message instead of a red "refresh failed" banner the user never triggered. */}
      {catalogError && (
        <Alert variant="danger" isInline title="Could not refresh prices" className="pf-v6-u-mb-md">
          {catalogError}
        </Alert>
      )}
      {categoriesError && (
        <Alert variant="warning" isInline title="Could not load category names" className="pf-v6-u-mb-md">
          The catalog is grouped using each product&apos;s built-in category name instead. ({categoriesError})
        </Alert>
      )}
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by product name..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={() => setSearchValue('')}
            />
          </ToolbarItem>
          <ToolbarItem>
            <Button
              variant="secondary"
              icon={<SyncAltIcon />}
              onClick={() => void refetch()}
              isLoading={catalogLoading}
              isDisabled={catalogLoading}
            >
              Refresh
            </Button>
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>
      {filteredProducts.length === 0 ? (
        <EmptyState titleText="No products match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Try a different search term, or clear the filter to see all products.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => setSearchValue('')}>
                Clear filter
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      ) : (
        <Table aria-label="Product catalog and prices" variant="compact">
          <Thead>
            <Tr>
              <Th>Product</Th>
              <Th>Billing</Th>
              <Th>Unit price (excl. VAT)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {displayRows.map((row) =>
              row.kind === 'group' ? (
                <Tr key={`group-${row.categoryId}`} className="ancalagon-product-group-header">
                  <Td colSpan={COLUMN_COUNT}>
                    {row.categoryName}{' '}
                    <Content component="small">
                      ({row.count} {row.count === 1 ? 'product' : 'products'})
                    </Content>
                  </Td>
                </Tr>
              ) : (
                <Tr key={row.product.id}>
                  <Td dataLabel="Product">
                    <div>{row.product.label}</div>
                    <Content component="small">{row.product.product_key}</Content>
                  </Td>
                  <Td dataLabel="Billing">{formatRecurring(row.product.recurring)}</Td>
                  <Td dataLabel="Unit price (excl. VAT)">
                    {renderPriceCell(row.product.pricing, row.product.recurring)}
                  </Td>
                </Tr>
              ),
            )}
          </Tbody>
        </Table>
      )}
    </>
  );
}

export default TenantProductPricesTab;
